import React, { useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';

import SearchResult from './SearchResult';
import RootStore from '../../store/RootStore';
import { IComponentFormOcr } from '../../component';
import SearchRow, { DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH } from './SearchRow';
import { resolveLocaleText } from '../../utils/localeText';
import {
  fetchExhibitorList,
  IExhibitorItem,
} from '../ComponentExhibitor/AllForExhibitor';

interface IAllForOcr extends IComponentFormOcr {
  rootStore?: RootStore;
}

type SearchMode = 'text' | 'image';
const SearchModeMap = {
  文本: 'text',
  图片: 'image',
};

const IMAGE_SEARCH_RESIZE_WIDTH = DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH;
const SEARCH_RESULT_RESTORE_KEY = 'jfoto_ocr_search_result';

interface ISearchResultRestoreState {
  pageKey: string;
  keyword: string;
  mode: SearchMode;
  restoreOnReturn?: boolean;
}

const getCurrentPageKey = () =>
  `${window.location.pathname}${window.location.search}`;

const saveSearchResultRestoreState = (state: ISearchResultRestoreState) => {
  try {
    window.sessionStorage.setItem(SEARCH_RESULT_RESTORE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('[AllForOcr] save search result state failed', error);
  }
};

const getSearchResultRestoreState = (): ISearchResultRestoreState | null => {
  try {
    const saved = window.sessionStorage.getItem(SEARCH_RESULT_RESTORE_KEY);
    if (!saved) return null;
    const state = JSON.parse(saved);
    return state && typeof state.keyword === 'string' ? state : null;
  } catch (error) {
    console.warn('[AllForOcr] read search result state failed', error);
    return null;
  }
};

const clearSearchResultRestoreState = () => {
  try {
    window.sessionStorage.removeItem(SEARCH_RESULT_RESTORE_KEY);
  } catch (error) {
    console.warn('[AllForOcr] clear search result state failed', error);
  }
};

const markSearchResultForRestore = () => {
  const saved = getSearchResultRestoreState();
  if (!saved || saved.pageKey !== getCurrentPageKey()) {
    return;
  }

  saveSearchResultRestoreState({
    ...saved,
    restoreOnReturn: true,
  });
};

const OCR_DEFAULT_TEXT = {
  placeholder: {
    'zh-CN': '输入关键词搜索',
    'en-US': 'Search by keyword',
  },
  imageSearchText: {
    'zh-CN': '以图搜图',
    'en-US': 'Search by image',
  },
  searchButtonText: {
    'zh-CN': '搜索',
    'en-US': 'Search',
  },
  searchingText: {
    'zh-CN': '正在搜索中...',
    'en-US': 'Searching...',
  },
  noResultText: {
    'zh-CN': '未找到相关图片',
    'en-US': 'No matching images found',
  },
  resultCountText: {
    'zh-CN': '共识别出 {count} 张图片',
    'en-US': '{count} images found',
  },
  similarCountText: {
    'zh-CN': '当前展示 {count} 张高相似图',
    'en-US': 'Showing {count} highly similar images',
  },
  batchDownloadText: {
    'zh-CN': '批量下载图片',
    'en-US': 'Batch download images',
  },
};

const formatLocaleTemplate = (
  template: string,
  params: Record<string, string | number> = {}
) => {
  return Object.keys(params).reduce((result, key) => {
    return result.replace(new RegExp(`\\{\\s*${key}\\s*\\}`, 'g'), String(params[key]));
  }, String(template || ''));
};

const resolveOcrLocaleText = (
  legacyValue: string | undefined,
  localeValue: any,
  fallbackLocaleMap: Record<string, string>,
  params?: Record<string, string | number>
) => {
  const template =
    resolveLocaleText(legacyValue, localeValue) ||
    resolveLocaleText('', fallbackLocaleMap);

  return formatLocaleTemplate(template, params);
};

const resolveSystemLocaleText = (
  fallbackLocaleMap: Record<string, string>,
  params?: Record<string, string | number>
) => {
  return formatLocaleTemplate(resolveLocaleText('', fallbackLocaleMap), params);
};

const AllForOcr = function (props: IAllForOcr) {
  const {
    rootStore,
    placeholder,
    placeholder_i18n,
    vectorSearchText,
    searchButtonText,
    searchButtonText_i18n,
    vectorThreshold,
    show_gallery_jumper,
    search_exhibitor,
  } = props;
  const { wxuserStore } = rootStore!;
  const [showResult, setShowResult] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exhibitors, setExhibitors] = useState<IExhibitorItem[]>([]);
  const [exhibitorTotal, setExhibitorTotal] = useState(0);
  const [searchMode, setSearchMode] = useState<SearchMode>(
    SearchModeMap.文本 as SearchMode
  );
  const [imagePreview, setImagePreview] = useState('');
  const restoredSearchRef = useRef(false);
  
  //  wxuserStore.aiSearch文本搜索
  const 用新的智能搜索文字描述加图片 = Number(window?.configDetail?.bool_vector) === 1;  
  const safeVectorThreshold = (() => {
    const result = Number(vectorThreshold);
    return Number.isNaN(result) ? 0 : result;
  })();
  const localizedPlaceholder = resolveOcrLocaleText(
    placeholder,
    placeholder_i18n,
    OCR_DEFAULT_TEXT.placeholder
  );
  const localizedImageSearchText =
    vectorSearchText || resolveSystemLocaleText(OCR_DEFAULT_TEXT.imageSearchText);
  const localizedSearchButtonText = resolveOcrLocaleText(
    searchButtonText,
    searchButtonText_i18n,
    OCR_DEFAULT_TEXT.searchButtonText
  );
  const localizedSearchingText = resolveSystemLocaleText(
    OCR_DEFAULT_TEXT.searchingText
  );
  const localizedNoResultText = resolveSystemLocaleText(
    OCR_DEFAULT_TEXT.noResultText
  );
  const localizedResultCountText = resolveSystemLocaleText(
    OCR_DEFAULT_TEXT.resultCountText
  );
  const localizedSimilarCountText = resolveSystemLocaleText(
    OCR_DEFAULT_TEXT.similarCountText
  );
  const localizedBatchDownloadText = resolveSystemLocaleText(
    OCR_DEFAULT_TEXT.batchDownloadText
  );
  const showGalleryJumper =
    typeof show_gallery_jumper === 'undefined'
      ? true
      : !!show_gallery_jumper;

  const resolveGalleryId = () => {
    const dataDetail: any = window.dataDetail || {};
    const gallery: any = window.gallery || {};
    return (
      dataDetail.id ||
      dataDetail.galleryid ||
      dataDetail.gallery_id ||
      gallery.id ||
      gallery.galleryid ||
      gallery.gallery_id
    );
  };

  const resolveOrderId = () => {
    const dataDetail: any = window.dataDetail || {};
    const gallery: any = window.gallery || {};
    const pageDetail: any = window.pageDetail || {};
    const configDetail: any = window.configDetail || {};
    return (
      dataDetail.orderid ||
      dataDetail.order_id ||
      dataDetail.orderDetail?.id ||
      dataDetail.order_detail?.id ||
      gallery.orderid ||
      gallery.order_id ||
      gallery.orderDetail?.id ||
      gallery.order_detail?.id ||
      pageDetail.orderid ||
      pageDetail.order_id ||
      configDetail.orderid ||
      configDetail.order_id
    );
  };

  const searchTextByLegacyOcr = async (number: string) => {
    const galleryId = Number(resolveGalleryId());
    if (!galleryId) {
      setList([]);
      return;
    }
    const result = await wxuserStore.ocr(number, galleryId);
    setList(formatList(result) as never[]);
  };

  const searchNumber = async (number: string) => {
    saveSearchResultRestoreState({
      pageKey: getCurrentPageKey(),
      keyword: number,
      mode: SearchModeMap.文本 as SearchMode,
      restoreOnReturn: false,
    });
    setShowResult(true);
    setSearchMode(SearchModeMap.文本 as SearchMode);
    setSearchInput(number || '');
    setImagePreview('');
    if (keyword !== number) {
      setList([]);
    }
    setKeyword(number);
    setLoading(true);
    setExhibitors([]);
    setExhibitorTotal(0);

    const searchExhibitors = async () => {
      if (!search_exhibitor) {
        return;
      }
      const orderId = resolveOrderId();
      if (!orderId) {
        return;
      }
      try {
        const pageSize = 100;
        const res: any = await fetchExhibitorList({
          order_id: String(orderId),
          page: 1,
          page_size: pageSize,
          name: number,
        });
        const firstPageItems =
          res && res.code === 200 && res.data && Array.isArray(res.data.items)
            ? res.data.items
            : [];
        const total = Number(res && res.data && res.data.total) || firstPageItems.length;
        // 搜索结果页只需要首屏展商数据；不能按 total 并发拉取全部分页，
        // 否则接口在 name 筛选未生效时会瞬间发出大量相同请求。
        const items = firstPageItems.filter(
          (item: IExhibitorItem) => item.status === 1 && item.gallery_id
        );
        setExhibitors(items);
        setExhibitorTotal(total);
      } catch (error) {
        console.error('[searchExhibitors]', error);
        setExhibitors([]);
        setExhibitorTotal(0);
      }
    };

    try {
      const searchImages = async () => {
        if (用新的智能搜索文字描述加图片) {
          const orderId = resolveOrderId();
          const galleryId = resolveGalleryId();
          if (!orderId) {
            console.warn(
              '[AllForOcr] orderId missing in vector text search, fallback to OCR',
              {
                galleryId,
                dataDetail: window.dataDetail,
                gallery: window.gallery,
              }
            );
            await searchTextByLegacyOcr(number);
            return;
          }
          const result = await wxuserStore.aiSearch({
            orderId,
            galleryId,
            queryText: number,
            threshould: safeVectorThreshold,
          });
          setList(formatList(result) as never[]);
        } else {
          await searchTextByLegacyOcr(number);
        }
      };
      await Promise.all([searchImages(), searchExhibitors()]);
    } catch (error) {
      console.error('[searchNumber]', error);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const searchImage = async (base64: string, file?: File) => {
    clearSearchResultRestoreState();
    setShowResult(true);
    setSearchMode(SearchModeMap.图片 as SearchMode);
    setSearchInput('');
    setKeyword('');
    setImagePreview(base64);
    setList([]);
    setExhibitors([]);
    setExhibitorTotal(0);
    setLoading(true);

    try {
      const orderId = resolveOrderId();
      const galleryId = resolveGalleryId();
      if (!orderId) {
        console.warn('[AllForOcr] orderId missing in vector image search', {
          galleryId,
          dataDetail: window.dataDetail,
          gallery: window.gallery,
        });
        setList([]);
        return;
      }
      const result = await wxuserStore.aiSearch({
        orderId,
        galleryId,
        imageBase64: base64,
        threshould: safeVectorThreshold,
      });
      setList(formatList(result) as never[]);
    } catch (error) {
      console.error('[searchImage]', error);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    clearSearchResultRestoreState();
    setShowResult(false);
    setSearchInput('');
    setKeyword('');
    setImagePreview('');
    setList([]);
    setExhibitors([]);
    setExhibitorTotal(0);
    setLoading(false);
  };

  useEffect(() => {
    if (restoredSearchRef.current || window.location.hash !== '#search-result') {
      return;
    }

    restoredSearchRef.current = true;
    const saved = getSearchResultRestoreState();
    if (
      saved &&
      saved.mode === SearchModeMap.文本 &&
      saved.pageKey === getCurrentPageKey() &&
      saved.restoreOnReturn &&
      saved.keyword
    ) {
      searchNumber(saved.keyword);
    }
    // 只在页面首次从 history 返回时恢复，避免搜索请求完成后重复执行。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatList = (list: any[]) => {
    if (!list) {
      return [];
    }
    //处理成需要的数据，注意，这会覆盖掉一部分数据，考虑到覆盖的数据暂时无用，也就无所谓了，实在需要再考虑在这里单独处理
    return list
      .map((one: any) => {
        if (one && Number(one.trash) > 0) {
          return null;
        }
        return one;
      })
      .filter(Boolean);
  };
  return (
    <div className="all-for-ocr">
      <SearchRow
        value={searchInput}
        onInputChange={setSearchInput}
        search={searchNumber}
        searchByImage={searchImage}
        clear={clearSearch}
        placeholder={localizedPlaceholder}
        enableImageSearch={用新的智能搜索文字描述加图片}
        imageSearchText={localizedImageSearchText}
        searchButtonText={localizedSearchButtonText}
        imageResizeWidth={IMAGE_SEARCH_RESIZE_WIDTH}
        searchLayout="button"
      />
      {showResult && (
        <SearchResult
          mode={searchMode}
          keyword={keyword}
          imagePreview={imagePreview}
          list={list}
          loading={loading}
          exhibitors={exhibitors}
          exhibitorTotal={exhibitorTotal}
          onReselectImage={(base64: string, file?: File) => {
            setSearchInput('');
            searchImage(base64, file);
          }}
          onSearchKeyword={searchNumber}
          placeholder={localizedPlaceholder}
          enableImageSearch={用新的智能搜索文字描述加图片}
          imageSearchText={localizedImageSearchText}
          searchButtonText={localizedSearchButtonText}
          loadingText={localizedSearchingText}
          noResultText={localizedNoResultText}
          recognizedCountText={localizedResultCountText}
          similarCountText={localizedSimilarCountText}
          batchDownloadText={localizedBatchDownloadText}
          imageResizeWidth={IMAGE_SEARCH_RESIZE_WIDTH}
          searchLayout="button"
          showGalleryJumper={showGalleryJumper}
          onExhibitorNavigate={markSearchResultForRestore}
          close={clearSearch}
        />
      )}
    </div>
  );
};

export default inject('rootStore')(observer(AllForOcr));
